import config, { getApiKey } from '../config/environment';

class AIGenerationService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
  }

  // Get headers for API requests
  getHeaders() {
    return {
      'accept': 'application/json',
      'x-api-key': getApiKey(),
      'Content-Type': 'application/json'
    };
  }

  // Generate role and goal using AI
  async generateRoleAndGoal(name, description, agentId = null, sessionId = null) {
    try {
      const requestBody = {
        request_type: "generate_role_goal",
        additional_data: {
          agent_id: agentId || "temp_agent_" + Date.now(),
          session_id: sessionId || "temp_session_" + Date.now(),
          streaming: true,
          name: name,
          description: description
        }
      };

      console.log('Sending AI generation request:', requestBody);

      const response = await fetch(`${this.baseURL}/ask/fouray`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let result = { role: '', goal: '' };
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        fullResponse += chunk;
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              // Handle both "data: " and "data: data: " formats
              let jsonStr = line.replace(/^data: /, '').trim();
              
              // If it still starts with "data: ", remove it again
              if (jsonStr.startsWith('data: ')) {
                jsonStr = jsonStr.replace(/^data: /, '').trim();
              }
              
              console.log('Processing line:', line);
              console.log('Extracted JSON string:', jsonStr);
              
              if (jsonStr) {
                const data = JSON.parse(jsonStr);
                console.log('Parsed data:', data);
                
                if (data.role) {
                  result.role = data.role;
                  console.log('Updated role:', data.role);
                }
                if (data.goal) {
                  result.goal = data.goal;
                  console.log('Updated goal:', data.goal);
                }
              }
            } catch (parseError) {
              console.warn('Failed to parse streaming data:', parseError);
              console.warn('Problematic line:', line);
            }
          }
        }
      }

      // If streaming didn't work, try to parse the full response
      if (!result.role && !result.goal) {
        console.log('Streaming failed, trying to parse full response:', fullResponse);
        try {
          // Try to extract JSON from the full response
          const jsonMatch = fullResponse.match(/\{.*"role".*"goal".*\}/s);
          if (jsonMatch) {
            const data = JSON.parse(jsonMatch[0]);
            console.log('Fallback parsing successful:', data);
            result = { role: data.role || '', goal: data.goal || '' };
          }
        } catch (fallbackError) {
          console.error('Fallback parsing also failed:', fallbackError);
        }
      }

      console.log('AI generation completed:', result);
      
      // Debug: Test parsing with the exact format from the screenshot
      if (!result.role && !result.goal) {
        console.log('Testing with sample data from screenshot...');
        const testLine = 'data: data: {"role": "The Product Descriptor agent is responsible for generating clear, engaging, and informative product descriptions that accurately reflect the features, benefits, and unique selling points of a product. This agent ensures that each description is tailored to the target audience, aligns with brand voice, and enhances the product\'s appeal to potential customers. The agent must also ensure that descriptions are optimized for search engines to improve product visibility online.", "goal": "Create product descriptions that highlight key features and benefits in a concise manner, ensuring each description is no longer than 150 words."}';
        
        try {
          let jsonStr = testLine.replace(/^data: /, '').trim();
          if (jsonStr.startsWith('data: ')) {
            jsonStr = jsonStr.replace(/^data: /, '').trim();
          }
          const testData = JSON.parse(jsonStr);
          console.log('Test parsing successful:', testData);
        } catch (testError) {
          console.error('Test parsing failed:', testError);
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error generating role and goal:', error);
      throw error;
    }
  }
}

const aiGenerationService = new AIGenerationService();
export default aiGenerationService;

